import { useTTS } from '../hooks/useTTS';

interface TTSControlsProps {
  content: string;
  themeStyles: { color: string; background: string };
  isDark: boolean;
  onParagraphChange?: (index: number) => void;
}

export function TTSControls({ content, themeStyles, isDark, onParagraphChange }: TTSControlsProps) {
  const {
    isPlaying,
    isPaused,
    currentIndex,
    totalParagraphs,
    voices,
    settings,
    updateSettings,
    play,
    pause,
    stop,
    skipTo,
  } = useTTS(content);

  // 通知父元件當前段落變化
  if (onParagraphChange && isPlaying) {
    onParagraphChange(currentIndex);
  }

  const zhVoices = voices.filter(v => v.lang.startsWith('zh') || v.lang.startsWith('cmn'));

  return (
    <div
      className="rounded-xl p-4 space-y-4"
      style={{
        backgroundColor: isDark ? '#2a2a2a' : '#f9fafb',
        border: `1px solid ${isDark ? '#444' : '#e5e7eb'}`,
      }}
    >
      {/* 播放控制 */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={() => skipTo(Math.max(0, currentIndex - 1))}
          disabled={currentIndex === 0}
          className="p-2 rounded-full disabled:opacity-30 transition-colors hover:bg-black/10"
          style={{ color: themeStyles.color }}
          title="上一段"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
          </svg>
        </button>

        <button
          onClick={isPlaying && !isPaused ? pause : play}
          className="p-3 rounded-full bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          title={isPlaying && !isPaused ? '暫停' : '播放'}
        >
          {isPlaying && !isPaused ? (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        <button
          onClick={stop}
          disabled={!isPlaying && !isPaused}
          className="p-2 rounded-full disabled:opacity-30 transition-colors hover:bg-black/10"
          style={{ color: themeStyles.color }}
          title="停止"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 6h12v12H6z" />
          </svg>
        </button>

        <button
          onClick={() => skipTo(Math.min(totalParagraphs - 1, currentIndex + 1))}
          disabled={currentIndex >= totalParagraphs - 1}
          className="p-2 rounded-full disabled:opacity-30 transition-colors hover:bg-black/10"
          style={{ color: themeStyles.color }}
          title="下一段"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
          </svg>
        </button>
      </div>

      {/* 進度顯示 */}
      {totalParagraphs > 0 && (
        <div className="text-center text-sm" style={{ color: themeStyles.color, opacity: 0.6 }}>
          段落 {currentIndex + 1} / {totalParagraphs}
        </div>
      )}

      {/* 速度調整 */}
      <div className="flex items-center gap-3">
        <span className="text-sm w-12" style={{ color: themeStyles.color }}>速度</span>
        <input
          type="range"
          min="0.5"
          max="2"
          step="0.1"
          value={settings.rate}
          onChange={(e) => updateSettings({ rate: Number(e.target.value) })}
          className="flex-1 accent-primary-600"
        />
        <span className="text-sm w-10 text-right" style={{ color: themeStyles.color, opacity: 0.6 }}>
          {settings.rate}x
        </span>
      </div>

      {/* 音調調整 */}
      <div className="flex items-center gap-3">
        <span className="text-sm w-12" style={{ color: themeStyles.color }}>音調</span>
        <input
          type="range"
          min="0.5"
          max="1.5"
          step="0.1"
          value={settings.pitch}
          onChange={(e) => updateSettings({ pitch: Number(e.target.value) })}
          className="flex-1 accent-primary-600"
        />
        <span className="text-sm w-10 text-right" style={{ color: themeStyles.color, opacity: 0.6 }}>
          {settings.pitch}
        </span>
      </div>

      {/* 語音選擇 */}
      {zhVoices.length > 0 && (
        <div className="flex items-center gap-3">
          <span className="text-sm w-12" style={{ color: themeStyles.color }}>語音</span>
          <select
            value={settings.voiceURI}
            onChange={(e) => updateSettings({ voiceURI: e.target.value })}
            className="flex-1 px-3 py-1.5 rounded-lg text-sm"
            style={{
              backgroundColor: isDark ? '#333' : '#fff',
              color: themeStyles.color,
              border: `1px solid ${isDark ? '#555' : '#d1d5db'}`,
            }}
          >
            {zhVoices.map((voice) => (
              <option key={voice.voiceURI} value={voice.voiceURI}>
                {voice.name} ({voice.lang})
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
